import type { ProjectRecord, Span, TimeBlockRecord } from '@wiztime/core';
import {
  createBlock,
  editBlocks,
  type CreateBlockOptions,
  type EditBlocksPatch,
} from './blocks';
import { upsertProject, type ProjectInput } from './projects';
import type { WriteSession } from './session';

/** What the new project is assigned to (2026-10b §2.5, §5.7). */
export type AssignTarget =
  | {
      /** Existing blocks (a block or every member of a merged group) change to the new project. */
      readonly kind: 'edit';
      readonly ids: readonly string[];
      /** Field changes committed together with the project (title, note, span). */
      readonly patch?: Omit<EditBlocksPatch, 'projectId'>;
    }
  | {
      /** The create context of the picker: a new confirmed block in the new project. */
      readonly kind: 'create';
      readonly date: string;
      readonly span: Span;
      readonly options?: CreateBlockOptions;
    };

export interface CreateProjectAndAssignResult {
  readonly project: ProjectRecord;
  /** Records written to blocks: the edited members, or the created block (empty when the guard hit). */
  readonly blocks: TimeBlockRecord[];
}

/**
 * "New project from the picker" as one command (2026-10b §2.5): the project is created and
 * then assigned in the same transaction, so a failure of the block write (the block was
 * deleted meanwhile, an invalid span) rolls the project back too — no orphan project — and a
 * single undo restores both. In the create context the exact-duplicate guard cannot hit a
 * project that has no blocks yet; if it ever does, only the project stays.
 */
export async function createProjectAndAssign(
  session: WriteSession,
  input: Omit<ProjectInput, 'id'>,
  target: AssignTarget,
): Promise<CreateProjectAndAssignResult> {
  const project = await upsertProject(session, input);
  if (target.kind === 'edit') {
    const blocks = await editBlocks(session, target.ids, { ...target.patch, projectId: project.id });
    return { project, blocks };
  }
  const created = await createBlock(session, target.date, target.span, project.id, target.options);
  return { project, blocks: created === null ? [] : [created] };
}
